import toPx from 'to-px';

import {
    waitUntil,
    WAIT_FOREVER,
} from 'async-wait-until';

import {
    gsapTimelinePromise,
    isScreenAtLeast,
    colors,
} from '../../../common/utils';

import els from './elements';

let activeIndex = 0;

let animating = false;

let animatedIn = false;

const isAnimating = () => animating;

const length = () => (
    els.$slides().length
);

const isAtBeginning = () => (
    activeIndex === 0
);

const willBeAtBeginning = (direction) => (
    activeIndex + direction === 0
);

const isAtEnd = () => (
    activeIndex === length() - 1
);

const willBeAtEnd = (direction) => (
    activeIndex + direction === length() - 1
);

const advanceForMobile = async (direction) => {

    animating = true;

    resetPlotAnimations();

    const nextIndex = activeIndex + direction;

    const $slides = els.$slides();

    const $lastSlide = els.$slide(activeIndex);
    const $lastSlideContent = els.$slideContent(activeIndex);
    const $lastSlideGraphic = els.$slideGraphic(activeIndex);

    const $nextSlide = els.$slide(nextIndex);
    const $nextSlideContent = els.$slideContent(nextIndex);
    const $nextSlideGraphic = els.$slideGraphic(nextIndex);

    const nextContentInitialY = (
        direction > 0
            ? (
                `+=${
                    2 * $nextSlideContent.outerHeight()
                }`
            )
            : (
                `-=${
                    (
                        $nextSlideContent.offset().top
                        + $nextSlideContent.outerHeight()
                    )
                    - (
                        $nextSlideGraphic.offset().top
                        + $nextSlideGraphic.outerHeight()

                    )
                }`
            )
    );

    const lastContentFinalY = (
        direction > 0
            ? (
                `-=${
                    (
                        $lastSlideContent.offset().top
                        + $lastSlideContent.outerHeight()
                    )
                    - (
                        $lastSlideGraphic.offset().top
                        + $lastSlideGraphic.outerHeight()

                    )
                }`
            )
            : (
                `+=${
                    2 * $lastSlideContent.outerHeight()
                }`
            )
    );

    const timeline = gsap.timeline();

    timeline.set($slides.not($lastSlide), { autoAlpha: 0 });

    timeline.set($lastSlideContent, { y: 0, autoAlpha: 1 });
    timeline.set($lastSlideGraphic, { zIndex: 1, autoAlpha: 1 });
    timeline.set($lastSlide, { autoAlpha: 1 });

    timeline.set($nextSlideContent, { y: nextContentInitialY, autoAlpha: 0 });
    timeline.set($nextSlideGraphic, { zIndex: 2, autoAlpha: 0 });
    timeline.set($nextSlide, { autoAlpha: 1 });

    timeline.to($lastSlideContent, { y: lastContentFinalY, autoAlpha: 0 });

    timeline.add(makePlotLineAnimation(nextIndex), '<');

    timeline.to($nextSlideContent, { y: 0, autoAlpha: 1 }, '<');

    timeline.to($nextSlideGraphic, { autoAlpha: 1 }, '<');

    timeline.set($lastSlideGraphic, { autoAlpha: 0 });
    timeline.set($lastSlideGraphic, { clearProps: 'zIndex' });
    timeline.set($nextSlideGraphic, { clearProps: 'zIndex' });

    timeline.set($lastSlide, { autoAlpha: 0 });

    await gsapTimelinePromise(timeline);

    activeIndex = nextIndex;
    animating = false;
};

const advanceForDesktop = async (direction) => {

    animating = true;

    resetPlotAnimations();

    const nextIndex = activeIndex + direction;

    const $slides = els.$slides();

    const $lastSlide = els.$slide(activeIndex);
    const $lastSlideContent = els.$slideContent(activeIndex);
    const $lastSlideGraphic = els.$slideGraphic(activeIndex);

    const $nextSlide = els.$slide(nextIndex);
    const $nextSlideContent = els.$slideContent(nextIndex);
    const $nextSlideGraphic = els.$slideGraphic(nextIndex);

    const nextContentInitialX = (
        direction > 0
            ? (
                `+=${
                    $nextSlideGraphic.offset().left
                    - $nextSlideContent.offset().left
                }`
            )
            : (
                `-=${
                    $nextSlideContent.outerWidth()
                    + $nextSlideContent.offset().left
                }`
            )
    );

    const lastContentFinalX = (
        direction > 0
            ? (
                `-=${
                    $lastSlideContent.outerWidth()
                    + $lastSlideContent.offset().left
                }`
            )
            : (
                `+=${
                    $lastSlideGraphic.offset().left
                    - $lastSlideContent.offset().left
                }`
            )
    );

    const timeline = gsap.timeline();

    timeline.set($lastSlideContent, { x: 0, autoAlpha: 1 });
    timeline.set($lastSlideGraphic, { zIndex: 1, autoAlpha: 1 });
    timeline.set($lastSlide, { autoAlpha: 1 });

    timeline.set($slides.not($lastSlide), { autoAlpha: 0 });

    timeline.set($nextSlideContent, { x: nextContentInitialX, autoAlpha: 0 });
    timeline.set($nextSlideGraphic, { zIndex: 2, autoAlpha: 0 });

    timeline.set($nextSlide, { autoAlpha: 1 });

    timeline.to($lastSlideContent, { x: lastContentFinalX, autoAlpha: 0 });

    timeline.add(makePlotLineAnimation(nextIndex), '<');

    timeline.to($nextSlideContent, { x: 0, autoAlpha: 1 }, '<');

    timeline.to($nextSlideGraphic, { autoAlpha: 1 }, '<');

    timeline.set($lastSlideGraphic, { autoAlpha: 0 });
    timeline.set($lastSlideGraphic, { clearProps: 'zIndex' });
    timeline.set($nextSlideGraphic, { clearProps: 'zIndex' });

    timeline.set($lastSlide, { autoAlpha: 0 });

    await gsapTimelinePromise(timeline);

    activeIndex = nextIndex;
    animating = false;
};

const advance = async (direction) => {

    if (animating) {
        return;
    }

    const $prev = els.$prevButton();
    const $next = els.$nextButton();

    const nextIndex = activeIndex + direction;

    if (
        nextIndex < 0
        || nextIndex > length() - 1
        || (
            nextIndex === activeIndex
            && activeIndex !== 0
        )
    ) {
        return;
    }

    if (direction < 0) {
        if (willBeAtBeginning(direction)) {
            $prev.attr('disabled', 'disabled');
        }
        if ($next.is(':disabled')) {
            $next.removeAttr('disabled');
        }
    } else {
        if (willBeAtEnd(direction)) {
            $next.attr('disabled', 'disabled');
        }
        if ($prev.is(':disabled')) {
            $prev.removeAttr('disabled');
        }
    }

    if (isScreenAtLeast('md')) {
        await advanceForDesktop(direction);
    } else {
        await advanceForMobile(direction);
    }
};

const seek = (destIndex) => {

    if (
        destIndex < 0
        || destIndex > length() - 1
    ) {
        return;
    }

    activeIndex = destIndex;

    resetSlideAnimations();

    resetPlotAnimations();
};

const end = async () => {

    if (isAtEnd()) {
        return;
    }

    seek(length() - 1);
};

const restart = async () => {

    if (isAtBeginning()) {
        return;
    }

    seek(0);
};

const makePlotLineAnimation = (destIndex) => {

    const delta = destIndex - activeIndex;

    const $plot = els.$plot();
    const $plotLineFill = els.$plotLineFill();
    const $dots = els.$dots();
    const $years = els.$years();
    const $rings = els.$plotDotRings();

    const subsetToAffect = ($elements) => {
        if (delta === 0) {
            return $elements.slice(0, destIndex + 1);
        }

        if (delta > 0) {
            return $elements.slice(activeIndex, destIndex + 1);
        }

        return (
            $elements
                .slice(destIndex, activeIndex + 1)
                .reverse()
        );
    };

    const $dotsToAffect = subsetToAffect($dots);
    const $yearsToAffect = subsetToAffect($years);
    const $ringsToAffect = subsetToAffect($rings);

    const timeline = gsap.timeline();

    timeline.timeScale($dotsToAffect.length * 0.25);

    $dotsToAffect.each((index, element) => {

        const $dot = $(element);
        const $year = $yearsToAffect.eq(index);
        const $ring = $ringsToAffect.eq(index);

        const dimension = (
            isScreenAtLeast('md')
                ? {
                    width: (
                        $dot.offset().left
                        + ($dot.outerWidth() * 0.5)
                    )
                }
                : {
                    height: (
                        (
                            $dot.offset().top
                            + ($dot.outerHeight() * 0.5)
                        )
                        - $plot.offset().top
                    ),
                }
        );

        if (delta >= 0) {

            timeline.to(
                $plotLineFill,
                {
                    ...dimension,
                    duration: 0.1,
                    ease: 'none',
                },
                '<'
            );

            timeline.to(
                $dot,
                {
                    backgroundColor: colors.yellow,
                    delay: 0.1,
                    duration: 0.1,
                    ease: 'power1.in',
                },
                '<'
            );

            timeline.to(
                $ring,
                {
                    autoAlpha: 1,
                    duration: 0.1,
                    ease: 'none',
                },
                '<'
            );

            timeline.to(
                $year,
                {
                    color: colors.yellow,
                    duration: 0.2,
                    ease: 'power1.out',
                },
                '<'
            );

            if (index < $dotsToAffect.length - 1) {

                timeline.to(
                    $ring,
                    {
                        autoAlpha: 0,
                        duration: 0.1,
                        ease: 'none',
                    },
                    '<'
                );
            }

        } else {

            if (index < $dotsToAffect.length - 1) {

                timeline.to(
                    $ring,
                    {
                        autoAlpha: 0,
                        duration: 0.1,
                        ease: 'none',
                    },
                    '<'
                );

                timeline.to(
                    $dot,
                    {
                        backgroundColor: colors.white,
                        duration: 0.2,
                        ease: 'power1.in',
                    },
                    '<'
                );

                timeline.to(
                    $year,
                    {
                        color: colors.white,
                        duration: 0.2,
                        ease: 'power1.out',
                    },
                    '<'
                );
            }

            timeline.to(
                $plotLineFill,
                {
                    ...dimension,
                    delay: 0.1,
                    duration: 0.1,
                    ease: 'none',
                },
                '<'
            );

            if (index === $dotsToAffect.length - 1) {

                timeline.to(
                    $ring,
                    {
                        autoAlpha: 1,
                        duration: 0.1,
                        ease: 'none',
                    }
                );
            }
        }
    });

    return timeline;
};

const prepareInitialScrollAnimation = async () => {

    if (animatedIn) {
        return;
    }

    animating = true;

    const $frame = els.$frame();
    const $firstSlide = els.$slide(0);
    const $firstSlideContent = els.$slideContent(0);
    const $firstSlideGraphic = els.$slideGraphic(0);
    const $plot = els.$plot();
    const $years = els.$years();

    gsap.set($firstSlideContent, { y: '25%', autoAlpha: 0 })
    gsap.set($firstSlideGraphic, { scale: 0.8, autoAlpha: 0 })
    gsap.set($years, { autoAlpha: 0 });

    gsap.set($firstSlide, { autoAlpha: 1 })

    let completedAnimations = 0;

    gsap.to(
        $firstSlideContent,
        {
            y: 0,
            autoAlpha: 1,
            ease: 'power2.out',
            duration: 0.6,
            scrollTrigger: {
                trigger: $firstSlideContent,
                start: (
                    isScreenAtLeast('md')
                        ? 'top 60%'
                        : 'top 80%'
                ),
            },
            onComplete: () => completedAnimations += 1,
        }
    );

    gsap.to(
        $firstSlideGraphic,
        {
            scale: 1,
            autoAlpha: 1,
            ease: 'power2.out',
            duration: 0.8,
            scrollTrigger: {
                trigger: $firstSlideGraphic,
                start: 'top 60%',
            },
            onComplete: () => completedAnimations += 1,
        }
    );

    const plotTimeline = gsap.timeline({
        scrollTrigger: {
            trigger: $frame,
            start: 'top center',
        },
        onComplete: () => completedAnimations += 1,
    });

    if (isScreenAtLeast('md')) {

        plotTimeline.fromTo(
            $plot,
            {
                y: '200%',
            },
            {
                y: 0,
                autoAlpha: 1,
                duration: 0.4,
                ease: 'power4.out',
            }
        );

        plotTimeline.fromTo(
            $years,
            {
                y: '25%',
            },
            {
                y: 0,
                autoAlpha: 1,
                stagger: 0.05,
                duration: 0.4,
                ease: 'power4.in',
            },
            '<'
        );

    } else {

        plotTimeline.fromTo(
            $plot,
            {
                x: '-200%',
            },
            {
                x: 0,
                autoAlpha: 1,
                duration: 0.4,
                ease: 'power4.out',
            }
        );

        plotTimeline.fromTo(
            $years,
            {
                x: '100%'
            },
            {
                stagger: 0.05,
                x: 0,
                autoAlpha: 1,
                duration: 0.4,
                ease: 'power4.in',
            },
            '<'
        );
    }

    plotTimeline.add(
        makePlotLineAnimation(0)
    );

    await waitUntil(
        () => completedAnimations === 3,
        {
            timeout: WAIT_FOREVER,
            intervalBetweenAttempts: 20,
        }
    );

    animating = false;
    animatedIn = true;
};

const setSlidesWrapHeight = () => {

    const $wrap = els.$slidesWrap();
    const $slides = els.$slides();

    const maxSlideHeight = (
        $slides.toArray().reduce(
            (max, slide) => (
                Math.max(max, $(slide).outerHeight())
            ),
            0
        )
    );

    gsap.set($wrap, { height: maxSlideHeight });
};

const resetSlidesWrapHeight = () => {

    const $wrap = els.$slidesWrap();

    gsap.set($wrap, { clearProps: 'height' });
};

const setPlotSize = () => {

    const $plot = els.$plot();
    const $years = els.$years();
    const $points = els.$points();

    if (isScreenAtLeast('md')) {

        gsap.set(
            $plot,
            {
                height: $years.first().outerHeight() * 4,
            }
        );

    } else {

        const maxYearWidth = (
            $years.toArray().reduce(
                (max, year) => (
                    Math.max(max, $(year).outerWidth())
                ),
                0
            )
        );

        gsap.set(
            $plot,
            {
                width: (
                    maxYearWidth
                    + toPx('0.5rem')
                    + $points.first().outerWidth()
                ),
            }
        );
    }
};

const resetPlot = () => {

    const $plot = els.$plot();
    const $years = els.$years();

    gsap.set($plot, { autoAlpha: 0 });

    gsap.set($plot, { clearProps: 'width,height' });

    gsap.set($years, { clearProps: 'x,y,xPercent,yPercent' });

    gsap.set($plot, { autoAlpha: 1 });
};

const resetPlotAnimations = () => {

    const $plot = els.$plot();
    const $lineFill = els.$plotLineFill();

    const $years = els.$years();
    const $activeYears = els.$years().slice(0, activeIndex + 1);

    const $dots = els.$dots();
    const $rings = els.$plotDotRings();
    const $activeDot = els.$dot(activeIndex);
    const $activeDots = $dots.slice(0, activeIndex + 1);
    const $activeRing = $rings.eq(activeIndex);

    gsap.killTweensOf($years);
    gsap.killTweensOf($dots);
    gsap.killTweensOf($rings);
    gsap.killTweensOf($lineFill);

    const dimension = (
        isScreenAtLeast('md')
            ? {
                width: (
                    $activeDot.offset().left
                    + ($activeDot.outerWidth() * 0.5)
                )
            }
            : {
                height: (
                    (
                        $activeDot.offset().top
                        + ($activeDot.outerHeight() * 0.5)
                    )
                    - $plot.offset().top
                ),
            }
    );

    gsap.set($years, { color: colors.white });
    gsap.set($activeYears, { color: colors.yellow });

    gsap.set($dots, { backgroundColor: colors.white });
    gsap.set($activeDots, { backgroundColor: colors.yellow });

    gsap.set($rings, { autoAlpha: 0 });
    gsap.set($activeRing, { autoAlpha: 1 });

    gsap.set($lineFill, { ...dimension });
};

const resetSlideAnimations = () => {

    const $slides = els.$slides();
    const $slidesContents = els.$slidesContents();
    const $slidesGraphics = els.$slidesGraphics();

    const $activeSlide = els.$slide(activeIndex);

    gsap.set($slides, { autoAlpha: 0 });

    gsap.set($slidesContents, { clearProps: 'all'});

    gsap.set($slidesGraphics, { clearProps: 'all'});

    gsap.set($activeSlide, { autoAlpha: 1 });
};

const registerEventListeners = () => {

    els.$points()
        .on('click', (event) => {

            const $point = $(event.currentTarget);

            const index = $point.index();

            advance(index - activeIndex);
        })
        .on('mouseenter', (event) => {

            const $point = $(event.currentTarget);

            const index = $point.index();

            const $dot = els.$dot(index);
            const $year = els.$year(index);

            const timeline = gsap.timeline();

            timeline.to(
                $dot,
                {
                    scale: 1.1,
                    duration: 0.1,
                    ease: 'power1.in',
                },
            );

            timeline.to(
                $year,
                {
                    scale: 1.15,
                    ...(
                        isScreenAtLeast('md')
                            ? { y: `-${$year.height() * 0.2}` }
                            : { x:  `-${$year.width() * 0.2}` }
                    ),
                    delay: 0.05,
                    duration: 0.075,
                    ease: 'power1.in',
                },
                '<'
            );
        })
        .on('mouseleave', (event) => {

            const $point = $(event.currentTarget);

            const index = $point.index();

            const $dot = els.$dot(index);
            const $year = els.$year(index);

            const timeline = gsap.timeline();

            timeline.to(
                $year,
                {
                    scale: 1,
                    ...(
                        isScreenAtLeast('md')
                            ? { y: 0 }
                            : { x: 0 }
                    ),
                    delay: 0.05,
                    duration: 0.075,
                    ease: 'power1.in',
                },
            );

            timeline.to(
                $dot,
                {
                    scale: 1,
                    duration: 0.1,
                    ease: 'power1.in',
                },
                '<'
            );

        });
};

const prepare = () => {

    setSlidesWrapHeight();
    setPlotSize();

    if (animatedIn) {
        makePlotLineAnimation(activeIndex).progress(1).pause();
    }
};

const cleanUp = () => {

    resetSlidesWrapHeight();
    resetPlotAnimations();
    resetPlot();
    resetSlideAnimations();
};

export default {
    prepare,
    prepareInitialScrollAnimation,
    registerEventListeners,
    cleanUp,
    isAtBeginning,
    willBeAtBeginning,
    isAtEnd,
    willBeAtEnd,
    isAnimating,
    advance,
    end,
    restart,
};

